<script>
    import { onMount, tick } from 'svelte';
    import { createEventDispatcher } from 'svelte';
    import { versionStore } from '../../stores/versionStore.js';
    import { specStore, claimsStore } from '../../stores/editorsStore.js';
    import { diffSpecContent, diffText } from '../../services/diffService.js';
    import DraggableResizablePanel from '../shared/DraggableResizablePanel.svelte';
    
    const dispatch = createEventDispatcher();
    
    export let show = false;
    
    // Panel state
    
    // Load saved tab preference or default to 'claims' (based on user behavior)
    let activeTab = localStorage.getItem('diffPanelActiveTab') || 'claims';
    
    // Save tab preference when it changes
    $: if (activeTab) {
        localStorage.setItem('diffPanelActiveTab', activeTab);
    }
    let diffResult = null;
    let isCalculating = false;
    let mounted = false;
    let panelInteractive = false; // Prevent clicks until panel is fully ready
    
    $: currentVersion = $versionStore.currentVersion;
    $: lastSavedContent = $versionStore.lastSavedContent;
    
    // Make diff rendering reactive to tab changes
    // This ensures the content re-renders when switching between spec/claims tabs
    $: renderedDiffHtml = diffResult && activeTab ? renderDiff() : '';
    
    
    // Auto-refresh when panel is shown (after component is mounted)
    let previousShow = false;
    
    $: {
        if (show && mounted && !previousShow) {
            // Use tick to ensure DOM is ready
            tick().then(() => {
                handleRefresh();
                // Enable interactivity after a short delay to prevent accidental clicks
                setTimeout(() => {
                    panelInteractive = true;
                }, 300);
            });
        } else if (!show) {
            panelInteractive = false;
        }
        previousShow = show;
    }
    
    onMount(() => {
        // Mark as mounted
        mounted = true;
        
        // If panel is already shown when mounting, trigger refresh
        if (show && !previousShow) {
            handleRefresh();
            previousShow = true;
        }
    });
    
    
    // Calculate diff
    function calculateDiff() {
        if (!currentVersion && (!lastSavedContent.spec && !lastSavedContent.claims)) {
            diffResult = null;
            isCalculating = false;
            return;
        }
        
        isCalculating = true;
        
        try {
            // Get current content from stores
            const currentSpec = $specStore.content || '';
            const currentClaims = $claimsStore.content || '';
            
            // Compare with last saved version
            const savedSpec = lastSavedContent.spec || '';
            const savedClaims = lastSavedContent.claims || '';
            
            // Use advanced diffing for better results
            const specDiff = diffSpecContent(savedSpec, currentSpec);
            const claimsDiff = diffText(savedClaims, currentClaims);
            
            diffResult = {
                specDiff,
                claimsDiff,
                versionName: currentVersion ? currentVersion.name : 'Last Saved Version'
            };
        } catch (error) {
            console.error('Error calculating diff:', error);
            diffResult = {
                specDiff: { changes: [], unchanged: true, error: true },
                claimsDiff: [],
                versionName: 'Error',
                error: true
            };
        } finally {
            isCalculating = false;
        }
    }
    
    async function handleRefresh() {
        isRefreshing = true;
        // Clear diff result first to force re-render
        diffResult = null;
        isCalculating = true;
        
        // Use setTimeout to ensure UI updates
        await new Promise(resolve => setTimeout(resolve, 0));
        
        calculateDiff();
        isRefreshing = false;
    }
    
    function handleClose() {
        dispatch('close');
    }
    
    function renderDiff() {
        if (!diffResult) return '';
        
        if (activeTab === 'spec') {
            // Render structural diff for specification
            return renderStructuralDiff(diffResult.specDiff);
        } else {
            // Render text diff for claims
            return renderTextDiff(diffResult.claimsDiff);
        }
    }
    
    function renderStructuralDiff(structuralDiff) {
        if (!structuralDiff) {
            return '<div class="diff-no-changes">No specification diff data available</div>';
        }
        
        if (structuralDiff.unchanged) {
            return '<div class="diff-no-changes">No changes detected</div>';
        }
        
        if (structuralDiff.error) {
            return '<div class="diff-no-changes">Error generating diff</div>';
        }
        
        // Return the full document HTML with inline changes
        return `<div class="diff-full-document">${structuralDiff.fullDocumentHtml || ''}</div>`;
    }
    
    // These functions are no longer needed since we render the full document
    // with inline changes instead of individual change cards
    /*
    function formatStructuralChange(change) { ... }
    function renderNode(node) { ... }
    function renderInlineContent(content) { ... }
    */
    
    function renderTextDiff(textDiff) {
        if (!textDiff || textDiff.length === 0) {
            return '<div class="diff-no-changes">No changes detected</div>';
        }
        
        const lines = [];
        
        // Add column headers
        lines.push(`<div class="diff-header">
                <span class="diff-header-line">Line</span>
                <span class="diff-header-marker"></span>
                <span class="diff-header-content">Changes</span>
            </div>`);
        
        textDiff.forEach((change, index) => {
            // Add separator before this change if needed
            if (change.needsSeparator) {
                lines.push('<div class="diff-group-separator"></div>');
            }
            
            if (change.type === 'added') {
                lines.push(`<div class="diff-line diff-line-added" key="${index}">
                    <span class="diff-gutter diff-gutter-added">
                        <span class="diff-line-number">${change.newLineNumber || ''}</span>
                    </span>
                    <span class="diff-line-marker">+</span>
                    <span class="diff-line-content">${escapeHtml(change.content)}</span>
                </div>`);
            } else if (change.type === 'removed') {
                lines.push(`<div class="diff-line diff-line-removed" key="${index}">
                    <span class="diff-gutter diff-gutter-removed">
                        <span class="diff-line-number">${change.oldLineNumber || ''}</span>
                    </span>
                    <span class="diff-line-marker">-</span>
                    <span class="diff-line-content">${escapeHtml(change.content)}</span>
                </div>`);
            } else if (change.type === 'modified') {
                // Show the old line
                lines.push(`<div class="diff-line diff-line-removed" key="${index}-old">
                    <span class="diff-gutter diff-gutter-removed">
                        <span class="diff-line-number">${change.oldLineNumber}</span>
                    </span>
                    <span class="diff-line-marker">-</span>
                    <span class="diff-line-content">${renderInlineDiff(change.inlineDiff, true)}</span>
                </div>`);
                // Show the new line (no extra spacing)
                lines.push(`<div class="diff-line diff-line-added" key="${index}-new">
                    <span class="diff-gutter diff-gutter-added">
                        <span class="diff-line-number">${change.newLineNumber}</span>
                    </span>
                    <span class="diff-line-marker">+</span>
                    <span class="diff-line-content">${renderInlineDiff(change.inlineDiff, false)}</span>
                </div>`);
            }
        });
        
        return `<div class="diff-lines">${lines.join('')}</div>`;
    }
    
    // This function is no longer needed for full document view
    // as inline styling is done in the diffService
    
    function renderInlineDiff(inlineDiff, isOldLine) {
        return inlineDiff.map(segment => {
            const text = escapeHtml(segment.text);
            
            if (segment.type === 'equal') {
                return text;
            } else if (segment.type === 'delete' && isOldLine) {
                return `<span class="diff-inline-removed">${text}</span>`;
            } else if (segment.type === 'insert' && !isOldLine) {
                return `<span class="diff-inline-added">${text}</span>`;
            } else if (segment.type === 'delete' && !isOldLine) {
                // Don't show deletions in the new line
                return '';
            } else if (segment.type === 'insert' && isOldLine) {
                // Don't show insertions in the old line
                return '';
            }
            
            return text;
        }).join('');
    }
    
    function escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
    
    function formatChangeAsText(change) {
        const nodeToText = (node) => {
            if (!node) return '';
            if (node.type === 'text') return node.text || '';
            if (node.content) {
                return node.content.map(n => nodeToText(n)).join(' ');
            }
            return '';
        };
        
        switch (change.type) {
            case 'added':
            case 'removed':
                return nodeToText(change.content);
            case 'modified':
                return nodeToText(change.node);
            default:
                return '';
        }
    }
    
    function exportDiff(format = 'text') {
        if (!diffResult) return;
        
        const section = activeTab === 'spec' ? 'Specification' : 'Claims';
        let content = '';
        
        switch (format) {
            case 'text':
                content = `Diff: Current vs ${diffResult.versionName}\nSection: ${section}\n\n`;
                
                if (activeTab === 'spec') {
                    // Export structural diff
                    const specDiff = diffResult.specDiff;
                    if (specDiff.unchanged) {
                        content += 'No changes detected.';
                    } else {
                        specDiff.changes.forEach(change => {
                            content += `\n${change.type.toUpperCase()}:\n`;
                            content += formatChangeAsText(change);
                            content += '\n';
                        });
                    }
                } else {
                    // Export line-by-line diff for claims
                    diffResult.claimsDiff.forEach(change => {
                        if (change.type === 'added') {
                            content += `+${change.newLineNumber || ''} ${change.content}\n`;
                        } else if (change.type === 'removed') {
                            content += `-${change.oldLineNumber || ''} ${change.content}\n`;
                        } else if (change.type === 'modified') {
                            content += `-${change.oldLineNumber} ${change.oldContent}\n`;
                            content += `+${change.newLineNumber} ${change.newContent}\n`;
                        }
                    });
                }
                
                downloadFile(`diff-${section.toLowerCase()}.txt`, content, 'text/plain');
                break;
                
            case 'html':
                content = `<!DOCTYPE html>
<html>
<head>
    <title>Diff: ${section}</title>
    <style>
        body { font-family: system-ui, sans-serif; padding: 20px; }
        .diff-content { white-space: pre-wrap; }
        .added { background-color: var(--color-collab-added-bg, rgba(72, 187, 120, 0.15)); color: var(--color-collab-added-text, #22863a); padding: 4px; margin: 2px 0; display: block; }
        .removed { background-color: var(--color-collab-removed-bg, rgba(245, 101, 101, 0.15)); color: var(--color-collab-removed-text, #cb2431); text-decoration: line-through; padding: 4px; margin: 2px 0; display: block; }
        .modified { background-color: var(--color-collab-modified-bg, rgba(255, 193, 7, 0.1)); color: var(--color-collab-modified-text, #856404); padding: 4px; margin: 2px 0; display: block; }
        .unchanged { color: var(--color-text-tertiary, #666); }
    </style>
</head>
<body>
    <h1>Diff: Current vs ${diffResult.versionName}</h1>
    <h2>Section: ${section}</h2>
    <div class="diff-content">${renderDiff()}</div>
</body>
</html>`;
                downloadFile(`diff-${section.toLowerCase()}.html`, content, 'text/html');
                break;
                
            case 'markdown':
                content = `# Diff: Current vs ${diffResult.versionName}\n## Section: ${section}\n\n`;
                
                if (activeTab === 'spec') {
                    const specDiff = diffResult.specDiff;
                    if (specDiff.unchanged) {
                        content += '_No changes detected._';
                    } else {
                        specDiff.changes.forEach(change => {
                            content += `\n### ${change.type.charAt(0).toUpperCase() + change.type.slice(1)}\n`;
                            content += '```\n';
                            content += formatChangeAsText(change);
                            content += '\n```\n';
                        });
                    }
                } else {
                    content += '```diff\n';
                    diffResult.claimsDiff.forEach(change => {
                        if (change.type === 'added') {
                            content += `+${change.newLineNumber || ''} ${change.content}\n`;
                        } else if (change.type === 'removed') {
                            content += `-${change.oldLineNumber || ''} ${change.content}\n`;
                        } else if (change.type === 'modified') {
                            content += `-${change.oldLineNumber} ${change.oldContent}\n`;
                            content += `+${change.newLineNumber} ${change.newContent}\n`;
                        }
                    });
                    content += '```';
                }
                
                downloadFile(`diff-${section.toLowerCase()}.md`, content, 'text/markdown');
                break;
        }
    }
    
    function downloadFile(filename, content, mimeType) {
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }
    
    // Track if we're in the middle of calculating to prevent spurious closes
    let isRefreshing = false;
</script>

<DraggableResizablePanel
    {show}
    title="Document Changes"
    storageKey="diff-panel"
    theme="diff"
    initialPosition={{ x: window.innerWidth * 0.38 - 100, y: 80 }}
    initialWidth={Math.min(window.innerWidth * 0.5, 1000)}
    initialHeight={window.innerHeight - 200}
    minWidth={350}
    minHeight={400}
    maxWidth={1200}
    maxHeight={window.innerHeight - 120}
    draggable={true}
    resizable={true}
    on:close={handleClose}
>
    <svelte:fragment slot="header-actions">
        {#if diffResult}
            <span class="version-badge">{diffResult.versionName}</span>
        {/if}
    </svelte:fragment>
        
    <div class="diff-panel-content">
        <!-- Controls -->
        <div class="diff-controls">
            <div class="tabs">
                <button 
                    class="tab"
                    class:active={activeTab === 'spec'}
                    on:click|stopPropagation={() => activeTab = 'spec'}
                >
                    Specification
                </button>
                <button 
                    class="tab"
                    class:active={activeTab === 'claims'}
                    on:click|stopPropagation={() => activeTab = 'claims'}
                >
                    Claims
                </button>
            </div>
            
            <div class="action-buttons">
                <button 
                    class="action-button"
                    on:click={handleRefresh}
                    disabled={isCalculating}
                    title="Refresh diff"
                >
                    <i class="fas fa-sync-alt" class:fa-spin={isCalculating}></i>
                </button>
                
                <div class="export-dropdown">
                    <button 
                        class="action-button"
                        title="Export diff"
                    >
                        <i class="fas fa-download"></i>
                    </button>
                    <div class="export-menu">
                        <button on:click={() => exportDiff('text')}>
                            <i class="fas fa-file-alt"></i> Plain Text
                        </button>
                        <button on:click={() => exportDiff('html')}>
                            <i class="fas fa-file-code"></i> HTML
                        </button>
                        <button on:click={() => exportDiff('markdown')}>
                            <i class="fas fa-file-invoice"></i> Markdown
                        </button>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Content -->
        <div class="diff-content">
            {#if isCalculating}
                <div class="empty-state">
                    <i class="fas fa-sync-alt fa-spin"></i>
                    <p>Calculating differences...</p>
                </div>
            {:else if diffResult && diffResult.error}
                <div class="empty-state">
                    <i class="fas fa-exclamation-triangle"></i>
                    <p>Error calculating differences</p>
                    <p class="empty-hint">Please try refreshing</p>
                </div>
            {:else if diffResult}
                {#key activeTab}
                    <div class="diff-text diff-content-styles diff-{activeTab}" contenteditable="false">
                        {@html renderedDiffHtml}
                    </div>
                {/key}
            {:else}
                <div class="empty-state">
                    <i class="fas fa-code-compare"></i>
                    <p>No saved version to compare</p>
                    <p class="empty-hint">Save a version to see changes</p>
                </div>
            {/if}
    </div>
</DraggableResizablePanel>

<style>
    /* Panel content wrapper */
    .diff-panel-content {
        display: flex;
        flex-direction: column;
        height: 100%;
    }
    
    /* Override panel styling for diff theme */
    :global(.draggable-panel--diff) :global(.draggable-panel-content) {
        padding: 0;
        background: transparent;
    }
    
    /* Header badge */
    :global(.draggable-panel--diff) .version-badge {
        padding: calc(var(--spacing-1) / 2) var(--spacing-2);
        font-size: var(--font-size-xs);
        font-weight: 600;
        color: var(--color-collab-text);
        background: var(--color-collab-badge);
        border-radius: var(--spacing-3);
    }
    
    /* Dark mode overrides for diff panel */
    :global([data-theme="dark"]) :global(.draggable-panel--diff) {
        background: var(--color-surface-panel-collab-frosted);
        border-color: var(--color-collab-border-dark);
    }
    
    :global([data-theme="dark"]) :global(.draggable-panel--diff) :global(.draggable-panel-header) {
        background: var(--color-surface-header-collab-frosted);
        border-bottom-color: var(--color-collab-border-dark);
    }
    
    :global([data-theme="dark"]) :global(.draggable-panel--diff) .version-badge {
        color: var(--color-collab-text-dark);
        background: var(--color-collab-badge-dark);
    }
    
    
    .diff-controls {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: var(--spacing-3) var(--spacing-4);
        background: transparent;
        border-bottom: 1px solid var(--color-collab-border);
    }
    
    /* Dark mode styles for controls */
    :global([data-theme="dark"]) .diff-controls {
        background: transparent;
        border-bottom-color: var(--color-collab-border-dark);
    }
    
    .tabs {
        display: flex;
        gap: var(--spacing-1);
    }
    
    .tab {
        padding: calc(var(--spacing-2) * 0.75) var(--spacing-3);
        font-size: var(--font-size-xs-plus);
        font-weight: 500;
        color: var(--color-collab-text);
        background: transparent;
        border: 1px solid transparent;
        border-radius: var(--spacing-5);
        transition: all 0.2s;
        cursor: pointer;
    }
    
    .tab:hover {
        background: var(--color-collab-hover);
    }
    
    .tab.active {
        background: var(--color-collab-badge);
        border-color: var(--color-collab-border);
    }
    
    .action-buttons {
        display: flex;
        gap: var(--spacing-2);
    }
    
    .action-button {
        display: flex;
        justify-content: center;
        align-items: center;
        width: var(--spacing-8);
        height: var(--spacing-8);
        color: var(--color-collab-text);
        background: transparent;
        border: 1px solid var(--color-collab-border);
        border-radius: calc(var(--spacing-2) * 0.75);
        transition: all 0.2s;
        cursor: pointer;
    }
    
    .action-button:hover:not(:disabled) {
        background: var(--color-collab-hover);
        border-color: var(--color-collab-text);
    }
    
    .action-button:disabled {
        opacity: var(--opacity-disabled);
        cursor: not-allowed;
    }
    
    .export-dropdown {
        position: relative;
    }
    
    .export-menu {
        position: absolute;
        top: 100%;
        right: 0;
        z-index: 1000;
        display: none;
        min-width: calc(var(--spacing-4) * 9.375);
        margin-top: var(--spacing-1);
        background: var(--color-surface-content-collab);
        border: 1px solid var(--color-collab-border);
        border-radius: calc(var(--spacing-2) * 0.75);
        box-shadow: var(--shadow-md);
    }
    
    .export-dropdown:hover .export-menu {
        display: block;
    }
    
    .export-menu button {
        display: flex;
        align-items: center;
        width: 100%;
        padding: var(--spacing-2) var(--spacing-3);
        font-size: var(--font-size-xs-plus);
        text-align: left;
        color: var(--color-text);
        background: transparent;
        border: none;
        transition: background 0.2s;
        cursor: pointer;
        gap: var(--spacing-2);
    }
    
    .export-menu button:hover {
        background: var(--color-collab-hover);
    }
    
    .export-menu button:first-child {
        border-radius: calc(var(--border-radius-lg) * 0.75) calc(var(--border-radius-lg) * 0.75) 0 0;
    }
    
    .export-menu button:last-child {
        border-radius: 0 0 calc(var(--border-radius-lg) * 0.75) calc(var(--border-radius-lg) * 0.75);
    }
    
    .diff-content {
        flex: 1;
        padding: 0 var(--spacing-5) var(--spacing-5);
        overflow-y: auto;
        background: transparent;
    }
    
    /* Scrollbar styling - matching diff theme */
    .diff-content::-webkit-scrollbar {
        width: var(--spacing-2);
    }
    
    .diff-content::-webkit-scrollbar-track {
        background: var(--color-surface-content-collab);
        border-radius: var(--spacing-1);
    }
    
    .diff-content::-webkit-scrollbar-thumb {
        background: var(--color-collab-border);
        border: var(--border-width-2) solid var(--color-surface-content-collab);
        border-radius: var(--spacing-1);
    }
    
    .diff-content::-webkit-scrollbar-thumb:hover {
        background: var(--color-collab-hover);
    }
    
    .diff-text {
        font-family: var(--font-family-mono);
        font-size: var(--font-size-xs-plus);
        line-height: var(--line-height-relaxed);
        white-space: pre-wrap;
        overflow-wrap: break-word;
    }
    
    
    /* Diff content styles */
    .diff-content-styles {
        font-family: var(--font-family);
        font-size: var(--font-size-sm);
        line-height: calc(var(--line-height-relaxed) + 0.2);
    }
    
    .diff-content-styles :global(.diff-change) {
        padding: var(--spacing-3);
        margin: var(--spacing-3) 0;
        border: 1px solid transparent;
        border-radius: calc(var(--spacing-2) * 0.75);
    }
    
    .diff-content-styles :global(.diff-structural-added) {
        background-color: var(--color-collab-added-bg);
        border-color: var(--color-collab-added-text);
    }
    
    .diff-content-styles :global(.diff-structural-removed) {
        background-color: var(--color-collab-removed-bg);
        border-color: var(--color-collab-removed-text);
    }
    
    .diff-content-styles :global(.diff-structural-modified) {
        background-color: var(--color-collab-modified-bg);
        border-color: var(--color-collab-modified-border);
    }
    
    .diff-content-styles :global(.change-marker) {
        margin-bottom: var(--spacing-2);
        font-size: var(--font-size-xs);
        font-weight: 600;
        text-transform: uppercase;
        opacity: calc(var(--opacity-deselected) + 0.1);
        letter-spacing: 0.05em;
    }
    
    .diff-content-styles :global(.diff-no-changes) {
        padding: var(--spacing-10) var(--spacing-5);
        text-align: center;
        color: var(--color-text-secondary);
        font-style: italic;
    }
    
    /* Full document diff view styles */
    .diff-content-styles :global(.diff-full-document) {
        font-family: var(--font-family);
        font-size: var(--font-size-sm);
        line-height: var(--line-height-relaxed);
        color: var(--color-text);
    }
    
    .diff-content-styles :global(.diff-full-document p),
    .diff-content-styles :global(.diff-full-document h1),
    .diff-content-styles :global(.diff-full-document h2),
    .diff-content-styles :global(.diff-full-document h3),
    .diff-content-styles :global(.diff-full-document h4),
    .diff-content-styles :global(.diff-full-document h5),
    .diff-content-styles :global(.diff-full-document h6),
    .diff-content-styles :global(.diff-full-document li),
    .diff-content-styles :global(.diff-full-document blockquote) {
        margin: var(--spacing-3) 0;
    }
    
    /* Paragraph numbering styles for diff view */
    .diff-content-styles :global(.numbered-paragraph),
    .diff-content-styles :global(.regenerative-paragraph),
    .diff-content-styles :global(.boilerplate-paragraph),
    .diff-content-styles :global(.claim-assignment-paragraph) {
        position: relative;
    }
    
    /* Add paragraph numbers as ::before pseudo-elements */
    .diff-content-styles :global(.numbered-paragraph::before),
    .diff-content-styles :global(.regenerative-paragraph::before),
    .diff-content-styles :global(.boilerplate-paragraph::before),
    .diff-content-styles :global(.claim-assignment-paragraph::before) {
        position: relative;
        display: inline;
        margin-right: 4px;
        font-family: var(--font-family-mono);
        font-size: var(--font-size-xs);
        font-weight: bold;
        line-height: 1.6;
        color: var(--color-text-tertiary);
        user-select: none;
        content: attr(data-paragraph-number);
    }
    
    .diff-content-styles :global(.diff-full-document h1) { font-size: 1.8em; font-weight: 600; }
    .diff-content-styles :global(.diff-full-document h2) { font-size: 1.5em; font-weight: 600; }
    .diff-content-styles :global(.diff-full-document h3) { font-size: 1.3em; font-weight: 600; }
    .diff-content-styles :global(.diff-full-document h4) { font-size: 1.1em; font-weight: 600; }
    
    .diff-content-styles :global(.diff-full-document ul),
    .diff-content-styles :global(.diff-full-document ol) {
        padding-left: var(--spacing-6);
        margin: var(--spacing-3) 0;
    }
    
    /* Modification group styles */
    .diff-content-styles :global(.diff-modification-group) {
        padding: var(--spacing-3);
        margin: var(--spacing-4) 0;
        background: var(--color-surface-elevated);
        border: 1px solid var(--color-border);
        border-radius: var(--spacing-2);
    }
    
    .diff-content-styles :global(.diff-modification-separator) {
        padding: var(--spacing-1) 0;
        margin: var(--spacing-2) 0;
        font-size: var(--font-size-lg);
        text-align: center;
        color: var(--color-text-secondary);
    }
    
    /* Paragraph numbers are now included as text spans, no special CSS needed */
    
    /* Line-by-line diff styles for claims - Claude Code terminal style */
    .diff-content-styles :global(.diff-lines) {
        padding: 0;
        overflow-x: auto;
        font-family: var(--font-family-mono);
        font-size: var(--font-size-xs-plus);
        line-height: var(--line-height-snug);
        background: var(--color-surface-content);
        border-radius: var(--spacing-2);
    }
    
    /* Diff header styles */
    .diff-content-styles :global(.diff-header) {
        display: flex;
        align-items: center;
        padding: var(--spacing-2) var(--spacing-2) var(--spacing-2) var(--spacing-2);
        margin: var(--spacing-3) 0;
        font-size: var(--font-size-xs);
        font-weight: 600;
        text-transform: uppercase;
        color: var(--color-text-secondary);
        border-bottom: 1px solid var(--color-border);
        letter-spacing: 0.05em;
    }
    
    .diff-content-styles :global(.diff-header-line) {
        min-width: var(--spacing-12);
        padding-right: var(--spacing-1);
        text-align: right;
    }
    
    .diff-content-styles :global(.diff-header-marker) {
        width: var(--spacing-4);
    }
    
    .diff-content-styles :global(.diff-header-content) {
        flex: 1;
        padding-left: var(--spacing-1);
    }
    
    .diff-content-styles :global(.diff-line) {
        display: flex;
        align-items: stretch;
        min-height: calc(var(--line-height-snug) * 1em);
        padding: 0 var(--spacing-2);
        margin: 0;
    }
    
    /* Group separator for different change locations */
    .diff-content-styles :global(.diff-group-separator) {
        height: var(--spacing-3);
        margin: var(--spacing-2) 0;
        border-bottom: 1px solid var(--color-border-subtle);
    }
    
    .diff-content-styles :global(.diff-line-added) {
        background-color: transparent;
    }
    
    .diff-content-styles :global(.diff-line-removed) {
        background-color: transparent;
    }
    
    .diff-content-styles :global(.diff-gutter) {
        display: inline-flex;
        justify-content: flex-end;
        align-items: center;
        min-width: var(--spacing-12);
        padding-right: var(--spacing-1);
        color: var(--color-text-tertiary);
        user-select: none;
    }
    
    .diff-content-styles :global(.diff-gutter-added) {
        background-color: var(--color-collab-added-bg);
    }
    
    .diff-content-styles :global(.diff-gutter-removed) {
        background-color: var(--color-collab-removed-bg);
    }
    
    .diff-content-styles :global(.diff-line-number) {
        font-size: var(--font-size-xs);
        opacity: var(--opacity-deselected);
    }
    
    .diff-content-styles :global(.diff-line-marker) {
        display: inline-block;
        width: var(--spacing-4);
        font-weight: 600;
        text-align: center;
        user-select: none;
    }
    
    .diff-content-styles :global(.diff-line-added) :global(.diff-line-marker) {
        color: var(--color-collab-added-text);
    }
    
    .diff-content-styles :global(.diff-line-removed) :global(.diff-line-marker) {
        color: var(--color-collab-removed-text);
    }
    
    .diff-content-styles :global(.diff-line-content) {
        flex: 1;
        padding-left: var(--spacing-1);
        white-space: pre-wrap;
        overflow-wrap: break-word;
    }
    
    .diff-content-styles :global(.diff-line-added) :global(.diff-line-content) {
        color: var(--color-text);
    }
    
    .diff-content-styles :global(.diff-line-removed) :global(.diff-line-content) {
        color: var(--color-text);
        opacity: var(--opacity-deselected);
    }
    
    /* Inline diff highlights within modified lines - subtle like Claude Code */
    .diff-content-styles :global(.diff-inline-added) {
        padding: 0 2px;
        font-weight: 600;
        color: var(--color-collab-added-text);
        background-color: var(--color-collab-added-bg);
        border-radius: 2px;
    }
    
    .diff-content-styles :global(.diff-inline-removed) {
        padding: 0 2px;
        text-decoration: line-through;
        color: var(--color-collab-removed-text);
        background-color: var(--color-collab-removed-bg);
        border-radius: 2px;
        text-decoration-thickness: 1px;
    }
    
    /* Rich content styles within diffs */
    .diff-content-styles :global(.diff-paragraph) {
        margin: var(--spacing-2) 0;
        line-height: var(--line-height-relaxed);
    }
    
    .diff-content-styles :global(.diff-heading) {
        margin: var(--spacing-3) 0 calc(var(--spacing-2) * 0.75) 0;
        font-weight: 600;
    }
    
    .diff-content-styles :global(h1.diff-heading) { font-size: 1.8em; }
    .diff-content-styles :global(h2.diff-heading) { font-size: 1.5em; }
    .diff-content-styles :global(h3.diff-heading) { font-size: 1.3em; }
    .diff-content-styles :global(h4.diff-heading) { font-size: 1.1em; }
    
    .diff-content-styles :global(.diff-list) {
        padding-left: var(--spacing-6);
        margin: var(--spacing-2) 0;
    }
    
    .diff-content-styles :global(.diff-list li) {
        margin: var(--spacing-1) 0;
    }
    
    .diff-content-styles :global(strong) { font-weight: 600; }
    .diff-content-styles :global(em) { font-style: italic; }

    .diff-content-styles :global(code) {
        padding: calc(var(--spacing-1) / 2) var(--spacing-1);
        font-family: var(--font-family-mono);
        font-size: 0.9em;
        background: var(--color-surface-elevated);
        border-radius: calc(var(--spacing-1) * 0.75);
    }
    
    :global(.diff-added) {
        padding: calc(var(--spacing-1) / 2) 0;
        color: var(--color-collab-added-text);
        background-color: var(--color-collab-added-bg);
    }
    
    :global(.diff-removed) {
        padding: calc(var(--spacing-1) / 2) 0;
        text-decoration: line-through;
        color: var(--color-collab-removed-text);
        background-color: var(--color-collab-removed-bg);
    }
    
    :global(.diff-unchanged) {
        color: var(--color-text-secondary);
    }
    
    .empty-state {
        padding: var(--spacing-10) var(--spacing-5);
        text-align: center;
        color: var(--color-text-secondary);
    }
    
    .empty-state i {
        margin-bottom: var(--spacing-4);
        font-size: var(--font-size-4xl);
        color: var(--color-collab-text);
    }
    
    .empty-state p {
        margin: var(--spacing-2) 0;
    }
    
    .empty-hint {
        font-size: var(--font-size-xs-plus);
        color: var(--color-text-tertiary);
    }
    
    .resize-handle {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: var(--spacing-1);
        background: transparent;
        border: none;
        transition: background-color 0.2s;
        cursor: ew-resize;
    }
    
    .resize-handle:hover,
    .resize-handle:focus {
        background: var(--color-collab-border);
        outline: none;
    }
    
    /* Dark mode adjustments */
    :global([data-theme="dark"]) .diff-panel {
        background: var(--color-surface-panel-collab-frosted);
        border-color: var(--color-collab-border-dark);
    }
    
    :global([data-theme="dark"]) .diff-panel-header {
        background: var(--color-surface-header-collab-frosted);
    }
    
    :global([data-theme="dark"]) .diff-panel-title {
        color: var(--color-collab-text-dark);
    }
    
    :global([data-theme="dark"]) .version-badge {
        color: var(--color-collab-text-dark);
        background: var(--color-collab-badge-dark);
    }
    
    :global([data-theme="dark"]) .diff-panel-close {
        color: var(--color-collab-text-dark);
    }
    
    :global([data-theme="dark"]) .diff-panel-close:hover {
        background: var(--color-collab-hover-dark);
    }
    
    :global([data-theme="dark"]) .diff-controls {
        border-color: var(--color-collab-border-dark);
    }
    
    :global([data-theme="dark"]) .tab {
        color: var(--color-collab-text-dark);
    }
    
    :global([data-theme="dark"]) .tab:hover {
        background: var(--color-collab-hover-dark);
    }
    
    :global([data-theme="dark"]) .tab.active {
        background: var(--color-collab-badge-dark);
        border-color: var(--color-collab-border-dark);
    }
    
    :global([data-theme="dark"]) .action-button {
        color: var(--color-collab-text-dark);
        border-color: var(--color-collab-border-dark);
    }
    
    :global([data-theme="dark"]) .action-button:hover:not(:disabled) {
        background: var(--color-collab-hover-dark);
        border-color: var(--color-collab-text-dark);
    }
    
    :global([data-theme="dark"]) .export-menu {
        background: var(--color-surface-content-collab);
        border-color: var(--color-collab-border-dark);
    }
    
    :global([data-theme="dark"]) .export-menu button:hover {
        background: var(--color-collab-hover-dark);
    }
    
    :global([data-theme="dark"]) .diff-content::-webkit-scrollbar-track {
        background: var(--color-surface-content-collab-dark);
    }
    
    :global([data-theme="dark"]) .diff-content::-webkit-scrollbar-thumb {
        background: var(--color-collab-border-dark);
        border-color: var(--color-surface-content-collab-dark);
    }
    
    :global([data-theme="dark"]) .diff-content::-webkit-scrollbar-thumb:hover {
        background: var(--color-collab-hover-dark);
    }
    
    :global([data-theme="dark"]) :global(.diff-added) {
        color: var(--color-collab-added-text-dark);
        background-color: var(--color-collab-added-bg-dark);
    }
    
    :global([data-theme="dark"]) :global(.diff-removed) {
        color: var(--color-collab-removed-text-dark);
        background-color: var(--color-collab-removed-bg-dark);
    }
    
    :global([data-theme="dark"]) .empty-state i {
        color: var(--color-collab-text-dark);
    }
    
    :global([data-theme="dark"]) .resize-handle:hover,
    :global([data-theme="dark"]) .resize-handle:focus {
        background: var(--color-collab-border-dark);
    }
    
    
    /* Dark mode diff styles */
    :global([data-theme="dark"]) .diff-content-styles :global(.diff-structural-added) {
        background-color: var(--color-collab-added-bg-dark);
        border-color: var(--color-collab-added-text-dark);
    }
    
    :global([data-theme="dark"]) .diff-content-styles :global(.diff-structural-removed) {
        background-color: var(--color-collab-removed-bg-dark);
        border-color: var(--color-collab-removed-text-dark);
    }
    
    :global([data-theme="dark"]) .diff-content-styles :global(.diff-structural-modified) {
        background-color: var(--color-collab-modified-bg-dark);
        border-color: var(--color-collab-modified-border-dark);
    }
    
    :global([data-theme="dark"]) .diff-content-styles :global(span.diff-added) {
        color: var(--color-collab-added-text-dark);
        background-color: var(--color-collab-added-bg-dark);
        text-decoration-color: var(--color-collab-added-text-dark);
    }
    
    :global([data-theme="dark"]) .diff-content-styles :global(span.diff-removed) {
        color: var(--color-collab-removed-text-dark);
        background-color: var(--color-collab-removed-bg-dark);
    }
    
    /* Dark mode line diff styles */
    :global([data-theme="dark"]) .diff-content-styles :global(.diff-lines) {
        background: var(--color-surface-elevated);
    }
    
    :global([data-theme="dark"]) .diff-content-styles :global(.diff-header) {
        border-bottom-color: var(--color-border-subtle);
    }
    
    :global([data-theme="dark"]) .diff-content-styles :global(.diff-group-separator) {
        border-bottom-color: var(--color-border-subtle);
    }
    
    :global([data-theme="dark"]) .diff-content-styles :global(.diff-gutter-added) {
        background-color: var(--color-collab-added-bg-dark);
    }
    
    :global([data-theme="dark"]) .diff-content-styles :global(.diff-gutter-removed) {
        background-color: var(--color-collab-removed-bg-dark);
    }
    
    :global([data-theme="dark"]) .diff-content-styles :global(.diff-line-added) :global(.diff-line-marker) {
        color: var(--color-collab-added-text-dark);
    }
    
    :global([data-theme="dark"]) .diff-content-styles :global(.diff-line-removed) :global(.diff-line-marker) {
        color: var(--color-collab-removed-text-dark);
    }
    
    :global([data-theme="dark"]) .diff-content-styles :global(.diff-inline-added) {
        color: var(--color-collab-added-text-dark);
        background-color: var(--color-collab-added-bg-dark);
    }
    
    :global([data-theme="dark"]) .diff-content-styles :global(.diff-inline-removed) {
        color: var(--color-collab-removed-text-dark);
        background-color: var(--color-collab-removed-bg-dark);
    }
</style>